import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Bot, Send, MessageSquare, BarChart3, Brain, Shield,
  RefreshCw, FileText, X, Square, Paperclip, Image
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const AdminTradosAgentChat = () => {
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const chatEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const location = useLocation();

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Restore conversation from History "Continue in Chat"
  useEffect(() => {
    if (location.state?.sessionId) {
      setSessionId(location.state.sessionId);
      setMessages(location.state.messages || []);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Background ping to keep session alive
  useEffect(() => {
    const interval = setInterval(() => {
      api.get('/admin/trados-agent/ping').catch(() => {});
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!chatInput.trim() && pendingAttachments.length === 0) || chatLoading) return;

    const userMsg = chatInput.trim() || '(see attached files)';
    const attachments = [...pendingAttachments];
    setChatInput('');
    setPendingAttachments([]);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, attachments }]);
    setChatLoading(true);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');

    try {
      const body = { message: userMsg, session_id: sessionId };
      if (attachments.length > 0) {
        body.attachments = attachments;
      }
      const response = await fetch(`${API_URL}/api/admin/trados-agent/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
        return;
      }

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const dataStr = line.slice(6);
            try {
              const data = JSON.parse(dataStr);
              if (currentEvent === 'message') {
                setSessionId(data.session_id);
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: data.response,
                  model: data.model_used,
                  tokens: data.tokens,
                }]);
              } else if (currentEvent === 'error') {
                toast.error(data.detail || 'Agent error');
                setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${data.detail}`, error: true }]);
              }
            } catch { /* ignore parse errors for partial chunks */ }
            currentEvent = '';
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Request stopped by user.', error: true }]);
      } else {
        const detail = err.message || 'Failed to send message';
        toast.error(detail);
        setMessages(prev => [...prev, { role: 'assistant', content: `Error: ${detail}`, error: true }]);
      }
    } finally {
      setChatLoading(false);
      abortControllerRef.current = null;
    }
  };

  const stopChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
        continue;
      }
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await api.post('/admin/trados-agent/chat/upload', formData);
        setPendingAttachments(prev => [...prev, res.data]);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (idx) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const newChat = () => {
    setMessages([]);
    setSessionId(null);
    setPendingAttachments([]);
  };

  return (
    <AgentLayout>
      <div className="bg-white rounded-xl border border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 260px)' }}>
        {/* Chat header */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Trados</p>
              <p className="text-xs text-gray-500">Operations Agent</p>
            </div>
          </div>
          <button onClick={newChat} className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100">
            <RefreshCw className="w-3 h-3" /> New Chat
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mb-4 shadow-lg">
                <Bot className="w-9 h-9 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Hi, I'm Trados</h3>
              <p className="text-sm text-gray-500 max-w-md mb-6">
                Your AI operations agent. I can help analyze platform data, review conversations,
                suggest improvements, and manage compliance checks.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-lg w-full">
                {[
                  { text: 'What are the key platform metrics?', icon: BarChart3 },
                  { text: 'Review recent Brandon & Amanda conversations', icon: MessageSquare },
                  { text: 'Suggest LOI prompt improvements', icon: Brain },
                  { text: 'Run compliance check on recent signups', icon: Shield },
                ].map((q, i) => (
                  <button key={i}
                    onClick={() => { setChatInput(q.text); }}
                    className="flex items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-700 bg-gray-50 rounded-lg border border-gray-200 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 transition-all"
                  >
                    <q.icon className="w-4 h-4 flex-shrink-0" />
                    <span>{q.text}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] ${msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-2xl rounded-tr-sm px-4 py-2.5'
                : `${msg.error ? 'bg-red-50 border border-red-200' : 'bg-gray-100'} text-gray-900 rounded-2xl rounded-tl-sm px-4 py-2.5`
              }`}>
                {msg.attachments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {msg.attachments.map((att, j) => (
                      att.is_image ? (
                        <a key={j} href={att.url} target="_blank" rel="noopener noreferrer">
                          <img src={att.url} alt={att.filename} className="max-w-[200px] max-h-[150px] rounded-lg border border-white/20 object-cover" />
                        </a>
                      ) : (
                        <a key={j} href={att.url} target="_blank" rel="noopener noreferrer"
                          className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs ${msg.role === 'user' ? 'bg-indigo-500/40 text-white hover:bg-indigo-500/60' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="truncate max-w-[150px]">{att.filename}</span>
                        </a>
                      )
                    ))}
                  </div>
                )}
                <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                {msg.model && (
                  <div className={`text-xs mt-1 ${msg.role === 'user' ? 'text-indigo-200' : 'text-gray-400'}`}>
                    {msg.model}{msg.tokens?.total ? ` · ${msg.tokens.total} tokens` : ''}
                  </div>
                )}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  Trados is thinking...
                </div>
              </div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={sendMessage} className="px-4 py-3 border-t border-gray-200">
          {pendingAttachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              {pendingAttachments.map((att, i) => (
                <div key={i} className="relative group">
                  {att.is_image ? (
                    <img src={att.url} alt={att.filename} className="w-16 h-16 rounded-lg object-cover border border-gray-200" />
                  ) : (
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-50 border border-gray-200 rounded-lg text-xs text-gray-700">
                      <FileText className="w-3.5 h-3.5" />
                      <span className="truncate max-w-[100px]">{att.filename}</span>
                    </div>
                  )}
                  <button type="button" onClick={() => removeAttachment(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.rtf"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="flex gap-2 items-end">
            <button type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={chatLoading}
              className="px-3 py-2.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-colors disabled:opacity-50"
              title="Attach files (images, PDFs, documents)"
            >
              <Paperclip className="w-5 h-5" />
            </button>
            <textarea
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder="Ask Trados anything... (Shift+Enter for new line)"
              rows={15}
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              disabled={chatLoading}
            />
            <div className="flex flex-col gap-2">
              {chatLoading ? (
                <button type="button" onClick={stopChat}
                  className="px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                  title="Stop request"
                >
                  <Square className="w-4 h-4" />
                </button>
              ) : (
                <button type="submit" disabled={!chatInput.trim() && pendingAttachments.length === 0}
                  className="px-4 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentChat;
